import { effectiveAnnualUsageKwhForPricing } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import {
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    systemSizeKwFromSnapshot,
} from '../../../../helpers/systemDesignSolarMetrics';
import { getRoofSquaresFromSystemDesign } from '../../../../services/oneButtonServices/designServices';
import { isHoverJobAvailable } from '../../../../services/oneButtonServices/hoverServices';
import { countDesignArrayModules, resolveRoofSquares } from '../../../../helpers/productDesignFallbacks';
import {
    getOpportunityCustomers,
    opportunityHasSyncedTitleContact,
} from '../../../../helpers/titleContactStatus';
import { getPrimaryContactRecord } from './contactCreditStatus';

export type SystemDesignMissingSeverity = 'hard' | 'soft';

export type SystemDesignMissingItemId =
    | 'sales-person'
    | 'title-contacts'
    | 'primary-contact'
    | 'utility-details'
    | 'coverage-production-guarantee'
    | 'solar-design'
    | 'roof-design'
    | 'battery-design';

/** Stable display / fix order: Customer → Utility → Designs. */
export const SYSTEM_DESIGN_MISSING_FLOW_ORDER: readonly SystemDesignMissingItemId[] = [
    'sales-person',
    'title-contacts',
    'primary-contact',
    'utility-details',
    'solar-design',
    'roof-design',
    'battery-design',
    'coverage-production-guarantee',
] as const;

export type SystemDesignMissingItem = {
    id: SystemDesignMissingItemId;
    label: string;
    severity: SystemDesignMissingSeverity;
    severityLabel: string;
    /** 1-based step in the required flow (for banner ordering). */
    step?: number;
};

export type SystemDesignMissingInputs = {
    opportunityDetails: unknown;
    solarUtilityData: unknown;
    utilityCompanyName: string | null;
    utilityDetailsDirty: boolean;
    sfAnnualUsage: number | null | undefined;
    systemDesign: unknown;
    systemDesignLosses: unknown;
    priceQuoteOverrides?: unknown;
    priceQuoteDetails?: unknown;
    hoverJobId?: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function opportunityInterest(opportunityDetails: unknown): Record<string, unknown> {
    const opp = asRecord(opportunityDetails);
    return asRecord(opp?.OpportunityInterest ?? opp?.opportunityInterest) ?? {};
}

function activeOpportunityProducts(opportunityDetails: unknown): Record<string, unknown>[] {
    const opp = asRecord(opportunityDetails);
    const products = opp?.OpportunityProduct ?? opp?.opportunityProduct;
    if (!Array.isArray(products)) return [];
    return products.filter((p) => {
        const row = asRecord(p);
        if (!row) return false;
        return row.ActiveFlag !== false && row.activeFlag !== false;
    }) as Record<string, unknown>[];
}

function productTypeName(p: Record<string, unknown>): string {
    return String(p.Product_Type_Name ?? p.product_Type_Name ?? '')
        .trim()
        .toLowerCase();
}

export function opportunityHasSolarProduct(opportunityDetails: unknown): boolean {
    const interest = opportunityInterest(opportunityDetails);
    if (interest.Solar === true) return true;
    return activeOpportunityProducts(opportunityDetails).some((p) => {
        const name = productTypeName(p);
        return name === 'solar' || name.startsWith('solar');
    });
}

export function opportunityHasRoofProduct(opportunityDetails: unknown): boolean {
    const interest = opportunityInterest(opportunityDetails);
    if (
        interest.Roof === true ||
        interest.RoofOnly === true ||
        interest.RR === true ||
        interest.RRR === true
    ) {
        return true;
    }
    return activeOpportunityProducts(opportunityDetails).some((p) => {
        const name = productTypeName(p);
        return (
            name === 'roof' ||
            name === 'rr' ||
            name === 'rrr' ||
            name.includes('roof')
        );
    });
}

export function opportunityHasBatteryProduct(opportunityDetails: unknown): boolean {
    const interest = opportunityInterest(opportunityDetails);
    if (interest.Battery === true || interest.BatteryOnly === true) return true;
    return activeOpportunityProducts(opportunityDetails).some((p) => {
        const name = productTypeName(p);
        return name === 'battery' || name.includes('battery') || name.includes('storage');
    });
}

function designLossesAreMissing(losses: unknown): boolean {
    if (losses == null) return true;
    if (typeof losses !== 'object') return true;
    const keys = Object.keys(losses as Record<string, unknown>).filter(
        (k) => (losses as Record<string, unknown>)[k] != null,
    );
    return keys.length === 0;
}

function positiveNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function roofRecord(systemDesign: unknown): Record<string, unknown> | null {
    const sd = asRecord(systemDesign);
    if (!sd) return null;
    const interest = asRecord(sd.OpportunityInterest) ?? {};
    const preferRoofOnly = interest.RoofOnly === true && interest.Roof !== true && interest.RRR !== true;
    const primary = preferRoofOnly ? sd.RoofOnly : sd.Roof;
    const fallback = preferRoofOnly ? sd.Roof : sd.RoofOnly;
    const pick = (roof: unknown): Record<string, unknown> | null => {
        if (roof == null) return null;
        if (Array.isArray(roof)) {
            const first = roof.find((r) => asRecord(r));
            return asRecord(first);
        }
        return asRecord(roof);
    };
    return pick(primary) ?? pick(fallback);
}

function isSolarDesignIncomplete(systemDesign: unknown): boolean {
    const sd = asRecord(systemDesign) ?? {};
    const designData = parseDesignDataRecord(sd);
    const systemKw = systemSizeKwFromSnapshot(sd, designData);
    const multRaw = sd.Multiplier ?? sd.multiplier;
    const multiplier =
        multRaw != null && Number.isFinite(Number(multRaw)) ? Number(multRaw) : null;
    const productionKwh = resolveSolarAnnualProductionKwh({
        sd,
        designData,
        systemKw,
        multiplier,
        snapshotOnly: true,
    });
    const modules = countDesignArrayModules(systemDesign);
    const hasDesignArray =
        Array.isArray(sd.DesignArray) &&
        sd.DesignArray.length > 0 &&
        (modules == null || modules > 0);

    return (
        !hasDesignArray ||
        systemKw == null ||
        systemKw <= 0 ||
        productionKwh == null ||
        productionKwh <= 0
    );
}

/** Design Total column (Low + Normal) from saved roof — not override fields. */
function designRoofSquaresTotal(roof: Record<string, unknown> | null): number {
    if (!roof) return 0;
    const low = Number(roof.LowSlope ?? 0) || 0;
    const normal = Number(roof.NormalSlope ?? 0) || 0;
    const fromSlopes = low + normal;
    if (fromSlopes > 0) return fromSlopes;
    const roofSquare = Number(roof.RoofSquare ?? roof.TotalRoofSquares ?? roof.RoofSquares ?? 0) || 0;
    return roofSquare > 0 ? roofSquare : 0;
}

/** Override column total (Low + Normal overrides) or RoofSquareOverride. */
function roofOverrideSquaresTotal(
    roof: Record<string, unknown> | null,
    priceQuoteOverrides?: Record<string, unknown> | null,
    priceQuoteDetails?: Record<string, unknown> | null,
): number {
    const ovLowRaw =
        priceQuoteOverrides?.lowSlopeOverride ??
        roof?.Low_Slope_Override ??
        priceQuoteDetails?.Low_Slope_Override;
    const ovNormalRaw =
        priceQuoteOverrides?.normalSlopeOverride ??
        roof?.Normal_Slope_Override ??
        priceQuoteDetails?.Normal_Slope_Override;

    const ovLow =
        ovLowRaw === null || ovLowRaw === undefined || ovLowRaw === ''
            ? null
            : Number(ovLowRaw);
    const ovNormal =
        ovNormalRaw === null || ovNormalRaw === undefined || ovNormalRaw === ''
            ? null
            : Number(ovNormalRaw);

    const hasLow = ovLow != null && Number.isFinite(ovLow);
    const hasNormal = ovNormal != null && Number.isFinite(ovNormal);
    if (hasLow || hasNormal) {
        return (hasLow ? (ovLow as number) : 0) + (hasNormal ? (ovNormal as number) : 0);
    }

    const roofSquareOverride = positiveNumber(
        priceQuoteOverrides?.roofSquareOverride ??
            roof?.RoofSquareOverride ??
            priceQuoteDetails?.RoofSquareOverride,
    );
    return roofSquareOverride ?? 0;
}

/**
 * Roof squares are complete when design Total (Low+Normal) > 0,
 * or — when design total is missing/0 — override total > 0.
 */
function hasRoofSquaresForCompleteness(
    systemDesign: unknown,
    priceQuoteOverrides?: Record<string, unknown> | null,
    priceQuoteDetails?: Record<string, unknown> | null,
): boolean {
    const roof = roofRecord(systemDesign);
    if (designRoofSquaresTotal(roof) > 0) return true;
    if (roofOverrideSquaresTotal(roof, priceQuoteOverrides, priceQuoteDetails) > 0) return true;

    const resolved = resolveRoofSquares({
        systemDesign,
        priceQuoteOverrides: priceQuoteOverrides as any,
        priceQuoteDetails: priceQuoteDetails as any,
        includeRoofProduct: true,
    });
    // OneDraw building squares count as design Total when API roof slopes are empty.
    if (resolved.total > 0 && resolved.source === 'onedraw') return true;

    const fromDesign = getRoofSquaresFromSystemDesign(systemDesign as any);
    return fromDesign != null && fromDesign > 0;
}

function isRoofDesignIncomplete(
    systemDesign: unknown,
    priceQuoteOverrides?: Record<string, unknown> | null,
    priceQuoteDetails?: Record<string, unknown> | null,
): boolean {
    const roof = roofRecord(systemDesign);
    const overrides = priceQuoteOverrides ?? null;
    const details = priceQuoteDetails ?? null;

    const squaresOk = hasRoofSquaresForCompleteness(systemDesign, overrides, details);

    const pricePerSqFt = positiveNumber(
        overrides?.pricePerSqFt ??
            roof?.PricePerSqft ??
            roof?.PricePerSqFt ??
            details?.PricePerSqFeet ??
            details?.PricePerSqft,
    );

    const shingle = String(
        overrides?.selectedShingleAlt ??
            overrides?.selectedShingleId ??
            roof?.ShingleColor ??
            roof?.ProductNumber ??
            '',
    ).trim();

    // No roof object and no override shingle/price still counts as incomplete.
    if (!roof && !overrides?.selectedShingleId && !overrides?.selectedShingleAlt && pricePerSqFt == null) {
        return true;
    }

    return !squaresOk || pricePerSqFt == null || !shingle || shingle === '—';
}

function isBatteryDesignIncomplete(systemDesign: unknown): boolean {
    const sd = asRecord(systemDesign) ?? {};
    const batteryInfo = asRecord(sd.BatteryInformation) ?? {};
    const financeRows = Array.isArray(sd.OpportunityProductsFinanceInfo)
        ? (sd.OpportunityProductsFinanceInfo as Record<string, unknown>[])
        : [];
    const batteryFinance = financeRows.find((row) => {
        const name = String(row.Product_Type_Name ?? row.product_Type_Name ?? '')
            .trim()
            .toLowerCase();
        return name === 'battery' || name.includes('battery');
    });

    const manufacturer = String(
        batteryInfo.Manufacturer ?? batteryFinance?.Manufacturer ?? '',
    ).trim();
    const modelName = String(batteryInfo.Name ?? batteryFinance?.Name ?? '').trim();
    const quantity = positiveNumber(batteryInfo.Quantity ?? batteryFinance?.Quantity);

    return !manufacturer || manufacturer === '—' || !modelName || modelName === '—' || quantity == null;
}

const CONTACT_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i;
const CONTACT_ZIP_REGEX = /^[0-9]{5}$/;

function contactField(contact: Record<string, unknown> | null, ...keys: string[]): string {
    if (!contact) return '';
    for (const key of keys) {
        const value = contact[key];
        if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
}

/** Matches ContactManageModal basic required fields for one customer row. */
function isContactRecordIncomplete(contact: Record<string, unknown> | null): boolean {
    if (!contact) return true;
    const firstName = contactField(contact, 'FirstName', 'firstName');
    const lastName = contactField(contact, 'LastName', 'lastName');
    const email = contactField(contact, 'Email', 'email');
    const phone = contactField(contact, 'Phone', 'phone', 'MobilePhone', 'mobilePhone');
    if (!firstName || !lastName) return true;
    if (!email || !CONTACT_EMAIL_REGEX.test(email)) return true;
    if (phone.replace(/\D/g, '').length < 10) return true;

    const sameAsInstall =
        contact.SameAsInstallationAddress ??
        contact.sameAsInstallationAddress ??
        true;
    if (sameAsInstall === false || sameAsInstall === 'false') {
        const street = contactField(
            contact,
            'InsStreet',
            'insStreet',
            'MailingStreet',
            'mailingStreet',
        );
        const city = contactField(contact, 'InsCity', 'insCity', 'MailingCity', 'mailingCity');
        const state = contactField(
            contact,
            'InsState',
            'insState',
            'MailingState',
            'mailingState',
        );
        const zip = contactField(
            contact,
            'InsZipCode',
            'insZipCode',
            'MailingPostalCode',
            'mailingPostalCode',
            'MailingZipCode',
            'mailingZipCode',
            'ZipCode',
            'zipCode',
        );
        if (!street || !city || !state || !CONTACT_ZIP_REGEX.test(zip)) return true;
    }
    return false;
}

function isSalesPersonIncomplete(opportunityDetails: unknown): boolean {
    const opp = asRecord(opportunityDetails);
    const salesPerson = asRecord(opp?.SalesPerson ?? opp?.salesPerson);
    if (!salesPerson) return true;
    return ![
        salesPerson.Name,
        salesPerson.name,
        salesPerson.Phone,
        salesPerson.phone,
        salesPerson.Email,
        salesPerson.email,
        salesPerson.Sales_Division,
        salesPerson.Team,
    ].some((v) => v != null && String(v).trim() !== '');
}

function isTitleContactsIncomplete(opportunityDetails: unknown): boolean {
    return !opportunityHasSyncedTitleContact(opportunityDetails);
}

/** True when any opportunity contact fails required field validation (or none exist). */
function isCustomerContactsIncomplete(opportunityDetails: unknown): boolean {
    const customers = getOpportunityCustomers(opportunityDetails);
    if (customers.length === 0) {
        return isContactRecordIncomplete(getPrimaryContactRecord(opportunityDetails));
    }
    return customers.some((c) => isContactRecordIncomplete(c));
}

function pushHard(
    items: SystemDesignMissingItem[],
    id: SystemDesignMissingItemId,
    label: string,
) {
    items.push({
        id,
        label,
        severity: 'hard',
        severityLabel: 'HARD MISS — BLOCKS PRICING & QUOTES',
    });
}

const CUSTOMER_SECTION_MISSING_IDS: readonly SystemDesignMissingItemId[] = [
    'sales-person',
    'title-contacts',
    'primary-contact',
];

function sortMissingItemsByFlow(items: SystemDesignMissingItem[]): SystemDesignMissingItem[] {
    const rank = new Map(SYSTEM_DESIGN_MISSING_FLOW_ORDER.map((id, i) => [id, i]));
    return [...items]
        .sort((a, b) => (rank.get(a.id) ?? 99) - (rank.get(b.id) ?? 99))
        .map((item, index) => ({ ...item, step: index + 1 }));
}

export function computeSystemDesignMissingItems(input: SystemDesignMissingInputs): SystemDesignMissingItem[] {
    const items: SystemDesignMissingItem[] = [];
    const hasSolar = opportunityHasSolarProduct(input.opportunityDetails);
    const hasRoof = opportunityHasRoofProduct(input.opportunityDetails);
    const hasBattery = opportunityHasBatteryProduct(input.opportunityDetails);
    const hasOpportunity = asRecord(input.opportunityDetails) != null;

    if (!hasSolar && !hasRoof && !hasBattery && !hasOpportunity) return [];

    // 1) Customer section (sales → title → contacts)
    if (hasOpportunity) {
        if (isSalesPersonIncomplete(input.opportunityDetails)) {
            pushHard(items, 'sales-person', 'Sales person');
        }
        if (isTitleContactsIncomplete(input.opportunityDetails)) {
            pushHard(items, 'title-contacts', 'Title contacts');
        }
        if (isCustomerContactsIncomplete(input.opportunityDetails)) {
            pushHard(items, 'primary-contact', 'Customer contacts');
        }
    }

    // Gate later stages until customer prerequisites are complete.
    if (items.some((item) => CUSTOMER_SECTION_MISSING_IDS.includes(item.id))) {
        return sortMissingItemsByFlow(items);
    }

    if (!hasSolar && !hasRoof && !hasBattery) return sortMissingItemsByFlow(items);

    // 2) Utility (required when solar is on the opportunity)
    if (hasSolar) {
        const utilitySolar = ((input.solarUtilityData as { SolarUtility?: Record<string, unknown> } | null)
            ?.SolarUtility ?? {}) as Record<string, unknown>;
        const annualUsage = effectiveAnnualUsageKwhForPricing(utilitySolar, input.sfAnnualUsage);
        const utilityCompany = String(
            utilitySolar.UtilityCompanyName ??
                utilitySolar.utilityCompanyName ??
                input.utilityCompanyName ??
                '',
        ).trim();

        const utilityIncomplete =
            input.utilityDetailsDirty ||
            annualUsage == null ||
            annualUsage <= 0 ||
            !utilityCompany;

        if (utilityIncomplete) {
            pushHard(items, 'utility-details', 'Utility details');
        }

        if (isSolarDesignIncomplete(input.systemDesign)) {
            pushHard(items, 'solar-design', 'Solar design');
        } else if (designLossesAreMissing(input.systemDesignLosses)) {
            items.push({
                id: 'coverage-production-guarantee',
                label: 'Coverage / production guarantee',
                severity: 'soft',
                severityLabel: 'SOFT MISS',
            });
        }
    }

    // 3) Product designs for active product lines
    if (
        hasRoof &&
        !isHoverJobAvailable(input.hoverJobId) &&
        isRoofDesignIncomplete(
            input.systemDesign,
            asRecord(input.priceQuoteOverrides),
            asRecord(input.priceQuoteDetails),
        )
    ) {
        pushHard(items, 'roof-design', 'Roof design');
    }

    if (hasBattery && isBatteryDesignIncomplete(input.systemDesign)) {
        pushHard(items, 'battery-design', 'Battery design');
    }

    return sortMissingItemsByFlow(items);
}

export function hasHardSystemDesignMiss(items: SystemDesignMissingItem[]): boolean {
    return items.some((item) => item.severity === 'hard');
}

/** Sales / title / contacts must be complete before System & Design unlocks. */
export function isDashboardCustomerSectionReady(items: SystemDesignMissingItem[]): boolean {
    return !items.some(
        (item) => item.severity === 'hard' && CUSTOMER_SECTION_MISSING_IDS.includes(item.id),
    );
}

/** Customer + utility + designs must be clear before pricing / partner sync / quotes. */
export function isDashboardCommerceReady(items: SystemDesignMissingItem[]): boolean {
    return !hasHardSystemDesignMiss(items);
}

/** Map Products hub / system-card product name to its design hard-miss id. */
export function productDesignHardMissId(productName: string): SystemDesignMissingItemId | null {
    const name = String(productName ?? '')
        .trim()
        .toLowerCase();
    if (!name) return null;
    if (name === 'solar' || name.startsWith('solar')) return 'solar-design';
    if (name === 'roof' || name.startsWith('roof') || name === 'rr' || name === 'rrr') return 'roof-design';
    if (name === 'battery' || name.startsWith('battery')) return 'battery-design';
    return null;
}

export type QuoteProductType = 'Solar' | 'Roof' | 'Battery';

/** True when generating a quote for any of these products should be blocked. */
export function hasQuoteHardMissForProducts(
    productTypes: readonly (QuoteProductType | string)[],
    items: SystemDesignMissingItem[],
): boolean {
    if (!productTypes.length) return false;

    const sharedBlocked = items.some(
        (item) =>
            item.severity === 'hard' &&
            (CUSTOMER_SECTION_MISSING_IDS.includes(item.id) || item.id === 'utility-details'),
    );
    if (sharedBlocked) return true;

    return productTypes.some((productName) => {
        const id = productDesignHardMissId(String(productName));
        if (!id) return false;
        return items.some((item) => item.id === id && item.severity === 'hard');
    });
}

/** Human reason when quote generation is blocked for the given product scope. */
export function quoteBlockedReasonFromItems(
    productNames: string | readonly string[],
    items: SystemDesignMissingItem[],
): string | null {
    const names = (Array.isArray(productNames) ? productNames : [productNames]).filter(
        (name) => String(name ?? '').trim(),
    );

    const shared = items
        .filter(
            (item) =>
                item.severity === 'hard' &&
                (CUSTOMER_SECTION_MISSING_IDS.includes(item.id) || item.id === 'utility-details'),
        )
        .sort((a, b) => (a.step ?? 99) - (b.step ?? 99));
    if (shared.length > 0) {
        const first = shared[0];
        return `Complete “${first.label}” first (step ${first.step ?? 1}). Quotes unlock after customer and utility details.`;
    }

    for (const name of names) {
        const designId = productDesignHardMissId(String(name));
        if (!designId) continue;
        const ownDesign = items.find((item) => item.id === designId && item.severity === 'hard');
        if (ownDesign) {
            const productLabel = String(name).trim() || 'product';
            return `${ownDesign.label} is incomplete. Complete required design details before generating a ${productLabel} quote.`;
        }
    }

    return null;
}

/** True when Partner Sync for this product should be blocked. */
export function hasProductDesignHardMiss(
    productName: string,
    items: SystemDesignMissingItem[],
): boolean {
    return hasQuoteHardMissForProducts([productName], items);
}

/** Human reason for blocked Partner Sync (this product's design, else shared prerequisites). */
export function partnerSyncBlockedReasonFromItems(
    productName: string,
    items: SystemDesignMissingItem[],
): string | null {
    const productLabel = String(productName || 'product').trim() || 'product';
    const designId = productDesignHardMissId(productName);
    if (designId) {
        const ownDesign = items.find((item) => item.id === designId && item.severity === 'hard');
        if (ownDesign) {
            return `${ownDesign.label} is incomplete. Fill in required design details before Partner Sync for ${productLabel}.`;
        }
    }

    const shared = items
        .filter(
            (item) =>
                item.severity === 'hard' &&
                (CUSTOMER_SECTION_MISSING_IDS.includes(item.id) || item.id === 'utility-details'),
        )
        .sort((a, b) => (a.step ?? 99) - (b.step ?? 99));
    if (shared.length === 0) return null;
    const first = shared[0];
    return `Complete “${first.label}” first (step ${first.step ?? 1}). Partner Sync unlocks after customer and utility details.`;
}

const MISSING_ITEM_SHORT_LABEL: Record<SystemDesignMissingItemId, string> = {
    'sales-person': 'sales',
    'title-contacts': 'title',
    'primary-contact': 'contacts',
    'utility-details': 'utility',
    'coverage-production-guarantee': 'coverage',
    'solar-design': 'solar',
    'roof-design': 'roof',
    'battery-design': 'battery',
};

export function systemDesignFixFabLabel(items: SystemDesignMissingItem[]): string {
    if (items.length === 0) return '';
    const parts = items.map((item) => MISSING_ITEM_SHORT_LABEL[item.id] ?? item.id);
    return `${items.length} step${items.length === 1 ? '' : 's'} · ${parts.join(' → ')}`;
}

/** Dashboard section rail panel that owns the fix UI for a missing item. */
export type SystemDesignMissingSectionPanel = 'utility' | 'system' | 'customer';

export function sectionPanelForMissingItem(id: SystemDesignMissingItemId): SystemDesignMissingSectionPanel {
    if (id === 'utility-details') return 'utility';
    if (id === 'sales-person' || id === 'title-contacts' || id === 'primary-contact') return 'customer';
    return 'system';
}

export function missingItemsForSectionPanel(
    panel: SystemDesignMissingSectionPanel,
    items: SystemDesignMissingItem[],
): SystemDesignMissingItem[] {
    return items.filter((item) => sectionPanelForMissingItem(item.id) === panel);
}

export function sectionPanelHasMissingItems(
    panel: SystemDesignMissingSectionPanel,
    items: SystemDesignMissingItem[],
): boolean {
    return missingItemsForSectionPanel(panel, items).length > 0;
}
